import {makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import {ModalType, RequestState} from "data/enums";
import type {IModalPayload, IModalsStore} from "data/stores/modals/modals.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import {useNavigate} from "react-router-dom";
import {AnalyticService} from "data/utils/track_analytics";
import type {IContextualPromptBannerStore} from "data/stores/contextual_prompt_banner/contextual_prompt_banner.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import {createFanHubEvent, EventGUID} from "data/utils/analytics";
import type {IGetShareImageParams, IShareImage} from "data/providers/api/predictions.api.provider";
import {MEDIA_URL} from "data/constants";
import type {ISharingStore} from "data/stores/sharing/sharing.store";

interface IProps {
	gameFeedId: number;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IModalConfirmController extends ViewController<IProps> {
	i18n: ILocalizationStore;

	get isOpen(): boolean;

	get modalContent(): IModalPayload | null;

	get playerFullName(): string | undefined;

	get shareImage(): IShareImage["shareImage"] | null;

	get imageUrl(): string | undefined;

	get isLoading(): boolean;

	close: () => void;
	toLeaderboard: () => void;
	toLanding: () => void;
	downloadImage: () => void;
	shareLink: () => void;
}

@injectable()
export class ModalConfirmController implements IModalConfirmController {
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	@observable _gameFeedId: number = 0;
	private _disposers: (() => void)[] = [];
	@observable _requestState: RequestState = RequestState.IDLE;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) public _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.SharingStore) private _sharingStore: ISharingStore,
		@inject(Bindings.ContextualPromptBannerStore)
		public _contextualPromptBanner: IContextualPromptBannerStore
	) {
		makeAutoObservable(this);
	}

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.CONFIRM;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get playerFullName() {
		const playerId = this._modalsStore.modalContent?.playerId ?? 0;
		if (!playerId) return;

		const player = this._playerStore.getPlayerById(playerId);
		return `${String(player?.firstName)} ${String(player?.lastName)}`;
	}

	close = () => {
		this._modalsStore.hideModal();
		localStorage.removeItem("localPrediction");

		AnalyticService.trackAction({
			"action.name": "Player of the Game: Vote Submitted Modal: Close Click",
			"action.location": "Major League Baseball: Player of the Game: Vote Submitted Modal",
			"action.category": "Player of the Game",
			"action.element.text": "Close Submitted modal",
			"baseball.gamePk": String(this._gameFeedId),
		});
		this.checkContextualPromptBanner();
	};

	get shareImage() {
		return this._sharingStore.shareImage;
	}

	get imageUrl() {
		const shareImage = this.shareImage;
		if (!shareImage) return;
		return `${MEDIA_URL}${shareImage.fileName}`;
	}

	get isLoading() {
		return [RequestState.PENDING, RequestState.IDLE].includes(this._requestState);
	}

	toLeaderboard = () => {
		this.close();
		createFanHubEvent(EventGUID.PostpickModalToLeaderboard);
		this._navigate?.("/leaderboard");
	};

	toLanding = () => {
		this.close();
		createFanHubEvent(EventGUID.PostpickModalToAdditionalClick);
		this._navigate?.("/");
	};

	init({gameFeedId, navigate}: IProps) {
		this._gameFeedId = gameFeedId;
		this._navigate = navigate;

		this._disposers = [
			reaction(
				() => this.isOpen,
				(isOpen) => {
					if (isOpen) {
						this.fetchImage();
					}
				}
			),
		];
	}

	dispose() {
		this._disposers.forEach((d) => d());
	}

	private fetchImage() {
		const content = this.modalContent;
		const contestId = content?.contestId;
		if (contestId) {
			this._requestState = RequestState.PENDING;
			const params: IGetShareImageParams = {
				contestId,
			};

			if (!this._userStore.isAuthorized) {
				params.guestUid = this._userStore.guestUid;
			}

			void this._sharingStore
				.requestImage(params)
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});
					console.error(e);
				});
		}
	}

	private checkContextualPromptBanner = () => {
		if (this._predictionsStore.isFirstPrediction) {
			void this._contextualPromptBanner.check(this._userStore.uid).then(() => {
				this._predictionsStore.clearFirstPrediction();
			});
		}
	};

	downloadImage = () => {
		const filename = `${this.playerFullName}.png`;
		const url = this.imageUrl;
		if (!url) {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: "There was a problem with the download",
			});
			return;
		}

		const link = document.createElement("a");
		link.href = url;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		// fetch(url)
		// 	.then(response => {
		// 		if( !response.ok ) {
		// 			throw new Error('Network response was not ok');
		// 		}
		// 		return response.blob();
		// 	})
		// 	.then(blob => {
		// 		const link = document.createElement('a');
		// 		const objectURL = URL.createObjectURL(blob);
		// 		link.href = objectURL;
		// 		link.download = filename;
		// 		document.body.appendChild(link);
		// 		link.click();
		// 		document.body.removeChild(link);
		// 		URL.revokeObjectURL(objectURL);
		// 	})
		// 	.catch(() => {
		// 		this._modalsStore.showModal(ModalType.ERROR, {
		// 			message: 'There was a problem with the download'
		// 		})
		// 	});
	};

	shareLink = () => {
		if (navigator.share) {
			navigator
				.share({
					title: this.i18n.t("modal.confirm.share_title", "Thank You for Voting"),
					text: this.i18n.t(
						"modal.confirm.share_text",
						`My Vote for Player of the Game is: ${this.playerFullName}`,
						{
							playerFullName: this.playerFullName,
						}
					),
					url: this.imageUrl,
				})
				.then(() => console.log("Shared successfully"))
				.catch((err) => {
					if (err.name === "AbortError") return;
					this._modalsStore.showModal(ModalType.ERROR, {
						message: this.i18n.t(
							"modal.confirm.share_error",
							"There was a problem with the share"
						),
					});
					return;
				});
		} else {
			this._modalsStore.showModal(ModalType.ERROR, {
				message: this.i18n.t(
					"modal.confirm.share_error",
					"There was a problem with the share"
				),
			});
			return;
		}
	};
}
